// Compliance check predicates — the "do-not-productize" detectors as pure
// string/array functions, with NO throwing (that's packages/ai/compliance.ts).
//
// Two layers consume these patterns: suspension-rules DETECTS them in a
// prospect's existing data (returns flags); packages/ai/compliance REFUSES to
// PRODUCE them (throws ComplianceViolationError). This leaf holds the
// production-refusal predicates. Patterns are inlined (mirror local-seo-
// constants.ts) so it stays a `node --test`-runnable leaf; a behavioral
// alignment test keeps them in step with suspension-rules.

const GATING_PATTERNS: ReadonlyArray<RegExp> = [
  /\bif\s+you(?:'?re| are|\s+were)?\s+(?:happy|satisfied|pleased)\b[^.?!]{0,60}\b(?:review|rate|stars?)\b/i,
  /\b(?:loved|enjoyed|happy with)\b[^.?!]{0,40}\bleave\s+(?:us\s+)?a\s+(?:review|rating)\b/i,
  /\bonly\s+if\b[^.?!]{0,40}\b(?:review|stars?)\b/i,
];

const INCENTIVE_PATTERNS: ReadonlyArray<RegExp> = [
  /\b(?:discount|coupon|free|gift\s*card|cash|\$\d+|\d+%\s*off)\b[^.?!]{0,40}\b(?:for|in exchange for|when you (?:leave|write|post))\b[^.?!]{0,20}\breview\b/i,
  /\b(?:leave|write|post)\b[^.?!]{0,20}\breview\b[^.?!]{0,30}\b(?:and (?:get|receive|enjoy)|for a?n?)\b[^.?!]{0,20}\b(?:discount|coupon|free|gift|cash|\d+%\s*off)\b/i,
];

const STUFFING_MARKERS: ReadonlyArray<{ re: RegExp; label: string }> = [
  { re: /\bbest\b/i, label: '“best”' },
  { re: /\b#\s?1\b/i, label: '“#1”' },
  { re: /\bnumber\s+one\b/i, label: '“number one”' },
  { re: /\btop[\s-]?rated\b/i, label: '“top-rated”' },
  { re: /\bcheap(est)?\b/i, label: '“cheap”' },
  { re: /\baffordable\b/i, label: '“affordable”' },
  { re: /\b24[\s/]?7\b/i, label: '“24/7”' },
  { re: /\bnear\s+me\b/i, label: '“near me”' },
  { re: /\bemergency\b/i, label: '“emergency”' },
  { re: /\bsame[\s-]?day\b/i, label: '“same-day”' },
];

// Quality guardrail for AI-generated location pages — the guide's verified
// "8–12 real pages, NOT a thin flood" constraint.
export const MAX_LOCATION_PAGES = 12;
export const MIN_LOCATION_PAGE_CHARS = 200;

function firstMatch(text: string, patterns: ReadonlyArray<RegExp>): string | null {
  for (const re of patterns) {
    const m = re.exec(text);
    if (m) return m[0].trim().slice(0, 120);
  }
  return null;
}

/** The matched review-gating phrase, or null. */
export function findGatingLanguage(text: string): string | null {
  return firstMatch(text, GATING_PATTERNS);
}

/** The matched review-incentive phrase, or null. */
export function findIncentiveLanguage(text: string): string | null {
  return firstMatch(text, INCENTIVE_PATTERNS);
}

/** Promotional / keyword-stuffing markers present in a proposed GBP name. */
export function findNameStuffing(name: string): string[] {
  return STUFFING_MARKERS.filter((m) => m.re.test(name)).map((m) => m.label);
}

/** A reason the location-page set is a thin/duplicate flood, or null if it's OK. */
export function findThinFloodReason(
  pages: ReadonlyArray<{ title: string; textLength: number }>,
): string | null {
  if (pages.length > MAX_LOCATION_PAGES) {
    return `${pages.length} pages exceeds the ${MAX_LOCATION_PAGES}-page quality cap`;
  }
  const thin = pages.filter((p) => p.textLength < MIN_LOCATION_PAGE_CHARS);
  if (thin.length > 0) {
    return `${thin.length} page(s) are too thin (< ${MIN_LOCATION_PAGE_CHARS} chars of real content)`;
  }
  const titles = new Set(pages.map((p) => p.title.trim().toLowerCase()));
  if (titles.size < pages.length) {
    return 'duplicate page titles (near-duplicate flood)';
  }
  return null;
}

// --- D1 proof-gate (Gate 1): generate-time "real local evidence" heuristics --
//
// findThinFloodReason above rejects SHORT bodies and duplicate TITLES. This
// rejects the doorway SHAPE that survives it: city-swapped bodies, generic
// service-area filler, and pages with too few distinct content words to carry
// real local value. It inspects the page BODY. It cannot verify evidence is
// TRUE (that is Gate 2, at publish time, plus owner-supplied per-area detail) —
// it refuses the mechanical scaled-content pattern.

/** Distinct content words a page body needs (beyond area name + boilerplate)
 *  to count as carrying real local detail. Floor 12: the empirically-measured
 *  safe separating window is [8, 21] (real page bodies score 21-24 distinct
 *  words; filler tops out at 7), and false-rejecting a real customer page costs
 *  more than admitting rare borderline filler — so center below the tightest
 *  real fixture, not at the edge. Re-tune in Task A3 against real generations. */
export const MIN_LOCATION_UNIQUE_CONTENT_WORDS = 12;

// Generic service-area filler: present in doorway pages, never sufficient as
// real local evidence. Stripped before counting real content words.
const LOCATION_BOILERPLATE_PATTERNS: ReadonlyArray<RegExp> = [
  /\b(?:and\s+)?(?:the\s+)?surrounding\s+areas?\b/gi,
  /\bwe\s+(?:proudly\s+)?serve\b/gi,
  /\bserving\s+(?:the\s+)?(?:greater\s+)?/gi,
  /\bno\s+matter\s+where\s+you\s+(?:are|live)\b/gi,
  /\b(?:areas?|neighborhoods?|communities)\s+we\s+(?:serve|cover)\b/gi,
  /\byour\s+(?:local|trusted|go-to)\b/gi,
  /\[\s*insert[^\]]*\]/gi, // unfilled prompt placeholder
];

const LOCATION_CONTENT_STOPWORDS: ReadonlySet<string> = new Set([
  'the', 'and', 'for', 'with', 'you', 'your', 'our', 'are', 'that', 'this',
  'from', 'have', 'will', 'can', 'all', 'any', 'get', 'out', 'who', 'how',
  'why', 'what', 'when', 'where', 'their', 'they', 'them', 'than', 'then',
  'into', 'over', 'more', 'most', 'some', 'such', 'here', 'there', 'about',
  'also', 'been', 'being', 'was', 'were', 'has', 'had', 'not', 'need', 'team',
]);

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Body normalized to a city-agnostic skeleton, for city-swap detection. */
function localSkeleton(body: string, area: string): string {
  const withoutArea = area
    ? body.replace(new RegExp('\\b' + escapeRegExp(area) + '\\b', 'gi'), ' ')
    : body;
  return withoutArea.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/** Count of distinct substantive words after stripping the area + boilerplate. */
function uniqueLocationContentWords(body: string, area: string): number {
  let text = area ? body.replace(new RegExp('\\b' + escapeRegExp(area) + '\\b', 'gi'), ' ') : body;
  for (const re of LOCATION_BOILERPLATE_PATTERNS) text = text.replace(re, ' ');
  const words = text.toLowerCase().match(/[a-z][a-z'-]{3,}/g) ?? [];
  return new Set(words.filter((w) => !LOCATION_CONTENT_STOPWORDS.has(w))).size;
}

/**
 * A reason the location-page set lacks real local evidence (doorway shape), or
 * null if every page carries distinct, substantive local content. Complements
 * findThinFloodReason (length / title) with body-level evidence heuristics.
 */
export function findNoLocalEvidenceReason(
  pages: ReadonlyArray<{ city?: string; title: string; body: string }>,
): string | null {
  // 1. City-swap flood: bodies identical once the area name is removed.
  const skeletons = pages.map((p) => localSkeleton(p.body, p.city ?? ''));
  if (new Set(skeletons).size < skeletons.length) {
    return 'near-duplicate city-swap bodies (same page with the area name swapped)';
  }
  // 2. Per-page real-content floor: too few distinct content words = filler.
  const hollow = pages.filter(
    (p) => uniqueLocationContentWords(p.body, p.city ?? '') < MIN_LOCATION_UNIQUE_CONTENT_WORDS,
  );
  if (hollow.length > 0) {
    return `${hollow.length} page(s) have no real local detail (< ${MIN_LOCATION_UNIQUE_CONTENT_WORDS} distinct content words beyond the area name and boilerplate)`;
  }
  return null;
}

/**
 * True when owner-supplied per-area local detail is placeholder / non-evidence
 * (empty, too short, only the area name, or generic filler). Phase B's owner
 * field uses this so a blank or boilerplate note cannot pass as real evidence.
 * NOT related to isPlaceholderValue (that is a NAP denylist in scripts/).
 */
export function isPlaceholderLocalDetail(text: string, area: string): boolean {
  if (text.trim().length < 20) return true;          // too short to be real
  return uniqueLocationContentWords(text, area) < 4; // only city + filler
}

/**
 * Entries whose detail is genuine (non-placeholder) local evidence — the
 * Phase B honesty filter over owner-entered per-area details (shape defined by
 * LocationEvidenceSchema in location-evidence-schemas.ts). Lives HERE, next to
 * its predicate, because shared leaves must stay free of runtime sibling
 * imports to remain bare `node --test` loadable. Structural param type keeps
 * the caller's entry type (e.g. LocationEvidenceEntry) intact.
 */
export function realEvidenceEntries<T extends { area: string; detail: string }>(
  entries: ReadonlyArray<T>,
): T[] {
  return entries.filter((e) => !isPlaceholderLocalDetail(e.detail, e.area));
}

/**
 * SERVER-AUTHORITATIVE area derivation (D1 proof-gate Phase B4): the set of
 * areas a location-page generation may cover, derived from the OWNER'S stored
 * evidence entries — never from the client. The requested list (what the owner
 * asked to generate this time) can only NARROW the set; an area without a real
 * (non-placeholder) stored entry never generates, and an empty/absent request
 * means "all evidenced areas". Canonical spelling comes from the stored entry
 * (the server's), not the request. Duplicate stored areas (case/space variants)
 * collapse to the first real entry. An empty result is the caller's cue to
 * REFUSE generation outright (no evidenced area → 422, M3 pattern).
 */
export function deriveEvidencedAreas<T extends { area: string; detail: string }>(
  requested: ReadonlyArray<string> | undefined,
  entries: ReadonlyArray<T>,
): { areas: string[]; details: T[] } {
  const norm = (s: string) => s.trim().toLowerCase();
  const wanted = requested && requested.length > 0 ? new Set(requested.map(norm)) : null;

  const details: T[] = [];
  const seen = new Set<string>();
  for (const entry of realEvidenceEntries(entries)) {
    const key = norm(entry.area);
    if (seen.has(key)) continue; // first real entry wins
    if (wanted && !wanted.has(key)) continue; // request narrows, never widens
    seen.add(key);
    details.push(entry);
  }
  return { areas: details.map((d) => d.area), details };
}
